<template>
  <div class="detail-page">
    <main class="detail-content">
      <div v-if="loading" class="state-card">正在加载检测进展...</div>
      <div v-else-if="errorMessage" class="state-card state-card--error">{{ errorMessage }}<Button @click="loadDetail">重新加载</Button></div>
      <template v-else>
        <section class="status-card">
          <div class="status-card__icon"><Icon type="ios-speedometer" /></div>
          <div class="status-card__content">
            <span>当前检测进展</span>
            <strong>{{ detail.statusName || '检测处理中' }}</strong>
            <p>检测编号：{{ detail.serialNum || '--' }}</p>
          </div>
        </section>

        <section class="progress-card">
          <div class="card-heading"><b>进展详情</b><span>共 {{ steps.length }} 个环节</span></div>
          <div v-for="(step, index) in steps" :key="step.name" :class="['progress-step', stepState(index)]">
            <div class="progress-step__rail">
              <span><Icon v-if="index < currentStep" type="ios-checkmark" />{{ index >= currentStep ? index + 1 : '' }}</span>
            </div>
            <div class="progress-step__text">
              <b>{{ step.name }}<em v-if="index === currentStep">当前</em></b>
              <small v-if="step.date">{{ step.date }}</small>
            </div>
          </div>
        </section>

        <section class="action-card">
          <button @click="goPatientInfo">
            <i class="action-icon"><Icon type="ios-person-outline" /></i>
            <span>检测信息</span>
          </button>
          <button @click="openReport">
            <i class="action-icon"><Icon type="ios-paper-outline" /></i>
            <span>{{ detail.reportPdfUrl ? '查看报告' : '检测报告' }}</span>
          </button>
          <button @click="goHealthArchive">
            <i class="action-icon"><Icon type="ios-list-box-outline" /></i>
            <span>{{ needsContinuation ? '继续填写问卷' : '健康档案' }}</span>
          </button>
          <button @click="openCustomerService">
            <i class="action-icon"><Icon type="ios-chatbubbles-outline" /></i>
            <span>在线客服</span>
          </button>
        </section>

        <section class="info-card">
          <div class="info-card__title"><b>检测人信息</b></div>
          <div><span>检测人</span><b>{{ detail.userName || '--' }}</b></div>
          <div><span>年龄</span><b>{{ detail.userAge || '--' }}</b></div>
          <div><span>联系电话</span><b>{{ detail.userPhone || '--' }}</b></div>
        </section>
        <registration-revoke-action :check-id="detail.serialNum || ''" :patient-id="detail.patientId || ''" :disabled="reportLoading" @busy-change="revokingRegistration = $event" @revoked="onRegistrationRevoked" />
      </template>
    </main>

    <div v-if="customerServiceVisible" class="service-modal" @click.self="closeCustomerService">
      <section class="service-dialog" role="dialog" aria-modal="true" aria-label="在线客服二维码">
        <button class="service-dialog__close" aria-label="关闭" @click="closeCustomerService">
          <Icon type="ios-close" />
        </button>
        <div class="service-dialog__icon"><Icon type="ios-chatbubbles-outline" /></div>
        <b>微信扫码联系在线客服</b>
        <p>长按保存二维码，再打开微信“扫一扫”，从相册识别进入小程序，点击“联系在线客服”。</p>
        <img :src="customerServiceQrCode" alt="小程序客服二维码" />
        <small>服务时间内，客服会尽快回复您</small>
      </section>
    </div>
  </div>
</template>

<script>
import Qs from 'qs'
import { ensureH5Session, retainedQuery } from '../utils/session'
import { isEmbeddedPage } from '../utils/parentWechat'
import { needsSurveyContinuation } from '../utils/entryRouting'
import RegistrationRevokeAction from '../components/RegistrationRevokeAction'
import { revokedHomeQuery } from '../utils/registrationRevoke'

const DEFAULT_STEPS = ['已绑定', '送检中', '已送检', '检测中', '检测完毕']
const CUSTOMER_SERVICE_URL = 'https://work.weixin.qq.com/kfid/kfcbef2b004f4efc8f8'
const CUSTOMER_SERVICE_QR_CODE = '/static/image/customer-service-miniapp-20260922.jpg'

export default {
  name: 'ProjectDetail',
  components: { RegistrationRevokeAction },
  data () {
    return {
      loading: true,
      revokingRegistration: false,
      reportLoading: false,
      errorMessage: '',
      detail: {},
      needsContinuation: false,
      currentStep: 0,
      steps: DEFAULT_STEPS.map(name => ({ name, date: '' })),
      customerServiceVisible: false,
      customerServiceQrCode: CUSTOMER_SERVICE_QR_CODE
    }
  },
  async created () {
    await this.loadDetail()
  },
  beforeRouteLeave (to, from, next) {
    if (this.revokingRegistration) { this.$Message.warning('正在撤销登记，请稍候'); return next(false) }
    next()
  },
  beforeRouteUpdate (to, from, next) {
    if (this.revokingRegistration) { this.$Message.warning('正在撤销登记，请稍候'); return next(false) }
    next()
  },
  methods: {
    async onRegistrationRevoked () {
      this.detail = {}
      await this.$router.replace({ path: '/home', query: revokedHomeQuery() })
    },
    async loadDetail () {
      this.loading = true
      this.errorMessage = ''
      try {
        await ensureH5Session(this)
        const res = await this.$http.post('/eps/snDetail', Qs.stringify({ snId: this.$route.params.projectId }))
        if (res.result !== '1' || !res.data) throw new Error(res.desc || '未查询到产品信息')
        this.detail = res.data
        this.buildProgress(res.data)
        this.needsContinuation = !res.data.reportPdfUrl && await needsSurveyContinuation(this.$http, res.data.patientId, res.data.status)
      } catch (error) {
        this.errorMessage = error.message || '检测详情加载失败'
      } finally {
        this.loading = false
      }
    },
    buildProgress (data) {
      const histories = String(data.changeHistories || '').split(';').filter(Boolean)
      const dateMap = {}
      histories.forEach(item => {
        const parts = item.split(',')
        if (parts[0]) dateMap[this.normalizeStepName(parts[0])] = this.formatDate(parts.slice(1).join(','))
      })
      this.steps = DEFAULT_STEPS.map(name => ({ name, date: dateMap[name] || '' }))
      let active = histories.length ? Math.min(histories.length - 1, 4) : 0
      this.steps.forEach((step, index) => {
        if (step.date) active = index
      })
      if (Number(data.status) === 10) active = 4
      if (Number(data.status) === 4) active = Math.max(active, 3)
      this.currentStep = active
    },
    normalizeStepName (name) {
      const aliases = {
        '未绑定': '已绑定',
        '待送检': '已绑定',
        '样本处理中': '检测中'
      }
      const value = String(name || '').trim()
      return aliases[value] || value
    },
    stepState (index) {
      if (index < this.currentStep) return 'is-completed'
      if (index === this.currentStep) return 'is-current'
      return 'is-pending'
    },
    formatDate (value) {
      return String(value || '').replace('T', ' ').replace(/-/g, '/').substring(0, 16)
    },
    goPatientInfo () {
      this.$router.push({
        path: '/intake',
        query: retainedQuery({
          patientId: this.detail.patientId || '',
          section: 'basic',
          returnTo: this.$route.fullPath
        })
      })
    },
    goHealthArchive () {
      this.$router.push({
        path: '/intake',
        query: retainedQuery({
          patientId: this.detail.patientId || '',
          section: 'health',
          returnTo: this.$route.fullPath
        })
      })
    },
    async openCustomerService () {
      if (isEmbeddedPage()) {
        this.customerServiceVisible = true
        return
      }
      const runtimeEnv = await this.detectCustomerServiceEnv()
      if (runtimeEnv === 'wechat') {
        window.location.href = CUSTOMER_SERVICE_URL
        return
      }
      this.customerServiceVisible = true
    },
    closeCustomerService () {
      this.customerServiceVisible = false
    },
    async detectCustomerServiceEnv () {
      const userAgent = window.navigator.userAgent || ''
      if (!/micromessenger/i.test(userAgent)) return 'browser'
      if (/miniprogram/i.test(userAgent)) return 'miniProgram'
      try {
        await this.ensureWechatScript()
        return await new Promise(resolve => {
          let resolved = false
          const finish = (runtimeEnv) => {
            if (resolved) return
            resolved = true
            resolve(runtimeEnv)
          }
          window.setTimeout(() => finish('wechat'), 1500)
          if (!window.wx || !window.wx.miniProgram || typeof window.wx.miniProgram.getEnv !== 'function') {
            finish('wechat')
            return
          }
          window.wx.miniProgram.getEnv(res => finish(res && res.miniprogram ? 'miniProgram' : 'wechat'))
        })
      } catch (error) {
        return 'wechat'
      }
    },
    ensureWechatScript () {
      return new Promise((resolve, reject) => {
        if (window.wx) {
          resolve()
          return
        }
        const current = document.getElementById('wechat-jssdk')
        if (current) {
          current.addEventListener('load', resolve, { once: true })
          current.addEventListener('error', reject, { once: true })
          return
        }
        const script = document.createElement('script')
        script.id = 'wechat-jssdk'
        script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'
        script.onload = resolve
        script.onerror = reject
        document.body.appendChild(script)
      })
    },
    async openReport () {
      if (this.reportLoading) return
      this.reportLoading = true
      try {
        const res = await this.$http.post('/h5/report/detail', Qs.stringify({
          ORDER_NUM: this.detail.serialNum || this.$route.query.serialNum || ''
        }))
        if (res.result !== '1' || !res.data || !res.data.reportPdfUrl) {
          const businessMessage = typeof res.data === 'string' ? res.data : ''
          throw new Error(businessMessage || res.desc || '报告尚未生成，请稍后再试')
        }
        this.$router.push({
          name: 'PdfPreview',
          query: { url: res.data.reportPdfUrl, title: '检测报告', serialNum: res.data.serialNum || '', personName: res.data.personName || '', returnTo: this.$route.fullPath }
        })
      } catch (error) {
        this.$Message.warning(error.message || '报告查询失败')
      } finally {
        this.reportLoading = false
      }
    }
  }
}
</script>

<style scoped>
.detail-page { min-height: 100vh; background: var(--wpc-bg); }
.detail-content { max-width: 620px; margin: 0 auto; padding: 14px 12px calc(82px + constant(safe-area-inset-bottom)); padding-bottom: calc(82px + env(safe-area-inset-bottom)); }
.state-card, .status-card, .progress-card, .action-card, .info-card { border-radius: var(--wpc-radius); background: #fff; border: 1px solid #eef1f5; }
.state-card { padding: 45px 20px; text-align: center; color: #8b95a5; }
.state-card--error { color: #d45d5d; }
.status-card { min-height: 124px; padding: 20px; box-sizing: border-box; display: flex; align-items: center; gap: 18px; background: linear-gradient(135deg, #fff 0%, #f4f9ff 100%); border-color: #deebfa; }
.status-card__icon { width: 64px; height: 64px; flex: 0 0 64px; border-radius: 50%; display: grid; place-items: center; color: var(--wpc-primary); background: #e7f2ff; font-size: 40px; }
.status-card__content { min-width: 0; }
.status-card__content > span { display: block; margin-bottom: 3px; color: var(--wpc-text-muted); font-size: 12px; }
.status-card strong { display: block; color: var(--wpc-text); font-size: 23px; font-weight: 600; }
.status-card p { margin: 10px 0 0; overflow: hidden; color: var(--wpc-text-secondary); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.progress-card { margin-top: 12px; padding: 18px 20px 12px; }
.card-heading { margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.card-heading b { font-size: 16px; }
.card-heading span { color: var(--wpc-text-muted); font-size: 12px; }
.progress-step { min-height: 58px; display: grid; grid-template-columns: 32px 1fr; column-gap: 10px; color: #101828; }
.progress-step__rail { position: relative; }
.progress-step__rail::after { content: ''; position: absolute; width: 2px; background: #dfe5ec; top: 27px; bottom: -32px; left: 12px; }
.progress-step.is-completed .progress-step__rail::after { background: #69bd91; }
.progress-step:last-child .progress-step__rail::after { display: none; }
.progress-step__rail span { width: 25px; height: 25px; box-sizing: border-box; border-radius: 50%; border: 1px solid #d7dce4; background: #fff; display: grid; place-items: center; color: #9aa4b2; font-size: 12px; }
.progress-step.is-completed .progress-step__rail span { border-color: #2fa36b; background: #2fa36b; color: #fff; font-size: 17px; }
.progress-step.is-current .progress-step__rail span { border: 2px solid #fff; background: var(--wpc-primary); color: #fff; box-shadow: 0 0 0 4px #dcecff; font-weight: 600; }
.progress-step.is-pending { color: #98a2b3; }
.progress-step__text { padding: 2px 0 14px; }
.progress-step__text b, .progress-step__text small { display: block; }
.progress-step__text b { font-size: 14px; }
.progress-step.is-current .progress-step__text b { color: var(--wpc-primary); font-size: 15px; }
.progress-step__text b em { display: inline-block; margin-left: 8px; padding: 2px 7px; border-radius: 9px; color: #fff; background: var(--wpc-primary); font-size: 10px; font-style: normal; font-weight: 500; vertical-align: 1px; }
.progress-step__text small { margin-top: 4px; color: #5f6c80; font-size: 12px; font-weight: 600; }
.action-card { margin-top: 12px; padding: 20px 8px 16px; display: grid; grid-template-columns: repeat(4, 1fr); }
.action-card button { min-width: 0; border: 0; background: none; display: flex; flex-direction: column; align-items: center; gap: 9px; color: #626f80; font-size: 12px; }
.action-icon { width: 44px; height: 44px; border-radius: 12px; display: grid; place-items: center; color: var(--wpc-primary); background: var(--wpc-primary-soft); font-size: 25px; }
.info-card { margin-top: 12px; padding: 4px 16px; }
.info-card div { min-height: 45px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f0f2f5; }
.info-card div:last-child { border-bottom: 0; }
.info-card .info-card__title { min-height: 48px; justify-content: flex-start; }
.info-card__title b { color: var(--wpc-text); font-size: 16px; font-weight: 600; }
.info-card span { color: #8993a2; }
.info-card b { max-width: 62%; overflow: hidden; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.service-modal { position: fixed; z-index: 3000; inset: 0; padding: 24px; display: flex; align-items: center; justify-content: center; background: rgba(20, 32, 48, .58); }
.service-dialog { position: relative; width: 100%; max-width: 330px; padding: 25px 22px 22px; box-sizing: border-box; border-radius: 18px; background: #fff; text-align: center; box-shadow: 0 18px 55px rgba(20, 45, 75, .24); }
.service-dialog__close { position: absolute; top: 8px; right: 8px; width: 36px; height: 36px; padding: 0; border: 0; color: #8a95a5; background: transparent; font-size: 30px; }
.service-dialog__icon { width: 46px; height: 46px; margin: 0 auto 10px; border-radius: 50%; display: grid; place-items: center; color: var(--wpc-primary); background: var(--wpc-primary-soft); font-size: 27px; }
.service-dialog > b { display: block; color: var(--wpc-text); font-size: 17px; }
.service-dialog > p { margin: 8px auto 14px; color: var(--wpc-text-secondary); font-size: 12px; line-height: 1.65; }
.service-dialog > img { display: block; width: 210px; height: 210px; margin: 0 auto; -webkit-touch-callout: default; user-select: auto; }
.service-dialog > small { display: block; margin-top: 12px; color: var(--wpc-text-muted); font-size: 11px; }

@media (max-width: 340px) {
  .detail-content { padding-right: 10px; padding-left: 10px; }
  .status-card { padding: 17px 14px; gap: 12px; }
  .status-card__icon { width: 54px; height: 54px; flex-basis: 54px; font-size: 34px; }
  .status-card strong { font-size: 20px; }
  .progress-card { padding-right: 16px; padding-left: 16px; }
  .action-card button { gap: 7px; font-size: 11px; }
  .action-icon { width: 40px; height: 40px; font-size: 23px; }
  .service-modal { padding: 16px; }
}
</style>



// WEBPACK FOOTER //
// src/views/OrderDetail.vue